import axios from "axios";
import { getData, storeData } from "@/lib/utils";
import store from "./store";
import { updateAuthUser, getMyCompany } from "./authSlice";

const api = axios.create({
  // baseURL: `https://medix-production-5f4f.up.railway.app/api/`, //YOUR_API_URL HERE
  // baseURL: `https://medix-oqmn.onrender.com/api/`, //YOUR_API_URL HERE
  baseURL: import.meta.env.VITE_URL_BACKEND,
  headers: {
    "Content-Type": "application/json",
  },
});
// Request queue to handle concurrent requests during token refresh
interface QueuedRequest {
  resolve: (value: any) => void;
  reject: (error: any) => void;
  config: any;
}

// Global state for token refresh
let isRefreshing = false;
let failedQueue: QueuedRequest[] = [];

// Process queued requests after token refresh
const processQueue = (error: any, token: string | null = null) => {
  failedQueue.forEach(({ resolve, reject, config }) => {
    if (error) {
      reject(error);
    } else {
      // Retry the original request with new token
      if (token && config.headers) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      resolve(api(config));
    }
  });
  failedQueue = [];
};

// Add request to queue
const addToQueue = (config: any): Promise<any> => {
  return new Promise((resolve, reject) => {
    failedQueue.push({ resolve, reject, config });
  });
};

// Refresh token function
const refreshToken = async () => {
  let refreshToken = getData("tokens")?.refreshToken;
  if (!refreshToken) {
    // localStorage.removeItem("tokens");
    // localStorage.removeItem("userInfo");
    throw new Error("No refresh token available");
  }

  isRefreshing = true;

  try {
    const response = await api.post(`/web/auth/refresh`, {
      refreshToken: refreshToken,
    });

    if (!response.data.isSuccess) {
      throw new Error("Token refresh failed");
    }

    const newTokens = response.data.data;
    storeData("tokens", newTokens);

    // Process queued requests with new token
    processQueue(null, newTokens.accessToken);

    return newTokens;
  } catch (err) {
    console.log("Token refresh failed");
    isRefreshing = false;

    // Clear user data and redirect
    store.dispatch(updateAuthUser(null));
    store.dispatch(getMyCompany(null));
    localStorage.removeItem("tokens");
    localStorage.removeItem("userInfo");

    // Process queued requests with error
    processQueue(err);

    throw err;
  } finally {
    isRefreshing = false;
  }
};

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = getData("tokens")?.accessToken;
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Don't attempt token refresh for login or refresh endpoints
    const isAuthEndpoint =
      originalRequest.url?.includes("/auth/login") ||
      originalRequest.url?.includes("/auth/refresh");

    if (
      axios.isAxiosError(error) &&
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isAuthEndpoint
    ) {
      originalRequest._retry = true;

      // Check if we have tokens before attempting refresh
      const tokens = getData("tokens");
      if (!tokens?.refreshToken) {
        // No tokens available, don't attempt refresh
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return addToQueue(originalRequest);
      }

      try {
        await refreshToken();
        // Retry the original request with new token
        const newToken = getData("tokens")?.accessToken;
        if (newToken) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }
        return api(originalRequest);
      } catch (refreshError) {
        // Don't reload the page, just reject the error
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
